import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  parsePhoneNumberFromString,
  getExampleNumber,
} from "libphonenumber-js";
import toast from "react-hot-toast";
import metadata from "libphonenumber-js/metadata.full.json";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { LanguageSwitcher } from "../../components/language/languageSwitcher/LanguageSwitcher";
import { LocationSelector } from "../../components/LocationSelector";
import { PhoneField } from "../../components/PhoneField/PhoneField";
import { extractErrorMessage } from "../../utils/extractErrorMessage";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

export default function BiographySalon() {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [workOptions, setWorkOptions] = useState({
    studio: false,
    travel: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(true);
  const selectedCountryRef = useRef("US");
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [addressDetails, setAddressDetails] = useState({
    country: "",
    city: "",
    address: "",
    state: "",
    postcode: "",
    fullAddress: "",
  });
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const userStatus = localStorage.getItem("user_status");

  const token = useMemo(() => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      ""
    ).trim();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenCurrency(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  useEffect(() => {
    const geoSuccess = (pos) => {
      const { latitude, longitude } = pos.coords;
      setPosition({ lat: latitude, lng: longitude });
    };

    const geoError = () => setPosition({ lat: 40.7128, lng: -74.006 });

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(geoSuccess, geoError);
    } else {
      geoError();
    }
  }, []);

  useEffect(() => {
    if (addressDetails.fullAddress) {
      setAddress(addressDetails.fullAddress);
    }
    if (addressDetails.city) {
      setCity(addressDetails.city);
    }
  }, [addressDetails]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoadingCurrency(true);

      try {
        if (!token) {
          toast.error(t("auth.sessionExpiredLoginAgain"));
          navigate("/sign-in");
          return;
        }

        const res = await fetch(`${BASE_URL}/service/currencies/`, {
          headers: buildHeaders(),
        });

        if (res.status === 401) {
          toast.error(t("auth.sessionExpiredLoginAgain"));
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(t("auth.failedToLoadCurrencies"));

        const data = await res.json();

        const formatted = data.map((item) => ({
          code: item.short_name,
          name: item.short_name,
          flag: item.icon || "",
        }));

        setCurrencies(formatted);

        if (formatted.length > 0) {
          setCurrency(formatted[0].code);
        }
      } catch {
        setCurrencies([
          { code: "USD", name: "USD ($)", flag: "https://flagcdn.com/us.svg" },
          { code: "EUR", name: "EUR (€)", flag: "https://flagcdn.com/eu.svg" },
          { code: "RUB", name: "RUB (₽)", flag: "https://flagcdn.com/ru.svg" },
        ]);
        setCurrency("USD");
      } finally {
        setLoadingCurrency(false);
      }
    };

    fetchCurrencies();
  }, [token, navigate, t]);

  const getNumberLengthRange = (country) => {
    try {
      const example = getExampleNumber(country, metadata);
      if (!example) return null;

      const digits = example.nationalNumber.length;

      return {
        min: digits - 1,
        max: digits + 1,
        example: example.nationalNumber,
      };
    } catch {
      return null;
    }
  };

  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/[^\d+\-\s]/g, "");
    setPhone(cleaned);

    const country = selectedCountryRef.current;
    const parsed = parsePhoneNumberFromString(cleaned, country);
    const lengthInfo = getNumberLengthRange(country);

    if (!parsed) {
      setErrors((prev) => ({
        ...prev,
        phone: t("auth.errorPhoneInvalid"),
      }));
      return;
    }

    const numLength = parsed.nationalNumber.length;

    if (lengthInfo) {
      if (numLength < lengthInfo.min) {
        setErrors((prev) => ({
          ...prev,
          phone: t("auth.errorPhoneTooShort", { min: lengthInfo.min, max: lengthInfo.max }),
        }));
        return;
      }

      if (numLength > lengthInfo.max) {
        setErrors((prev) => ({
          ...prev,
          phone: t("auth.errorPhoneTooLong", { min: lengthInfo.min, max: lengthInfo.max }),
        }));
        return;
      }
    }

    if (!parsed.isPossible() || !parsed.isValid()) {
      setErrors((prev) => ({
        ...prev,
        phone: t("auth.errorPhoneFormat"),
      }));
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next.phone;
      return next;
    });
  };

  const handleFullNameChange = (e) => {
    const value = e.target.value;
    const cleaned = value
      .replace(/[^a-zA-ZÀ-ÖØ-öø-ÿА-яЁёÀ-ÿÄäÖöÜüß\u0400-\u04FF\s-']/g, "")
      .replace(/\s+/g, " ")
      .replace(/^\s+/, "");
    setFullName(cleaned);

    if (errors.fullName) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.fullName;
        return next;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!fullName.trim() || fullName.length < 2) {
      newErrors.fullName = t("auth.errorFullName");
    }

    if (!companyName.trim() || companyName.length < 2) {
      newErrors.companyName = t("auth.errorCompanyName");
    }

    const parsed = parsePhoneNumberFromString(
      phone.replace(/[\s-]/g, ""),
      selectedCountryRef.current
    );

    if (!parsed || !parsed.isValid()) {
      newErrors.phone = t("auth.errorPhoneCountryInvalid");
    }

    if (!currency) {
      newErrors.currency = t("auth.errorCurrency");
    }

    if (!position) {
      newErrors.location = t("auth.errorLocationRequired");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length) {
      toast.error(t("auth.errorFixErrors"));
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      if (!token) {
        toast.error(t("auth.sessionExpiredLoginAgain"));
        navigate("/sign-in");
        return;
      }

      const parsed = parsePhoneNumberFromString(phone, selectedCountryRef.current);
      const normalizedPhone = parsed?.number || phone;

      const lat = position?.lat || 0;
      const lng = position?.lng || 0;
      const address_url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

      const userTimeZone = getUserTimeZone();

      const postBody = {
        full_name: fullName,
        company_name: companyName,
        phone_number: normalizedPhone,
        address_url,
        currency,
        worker_emails: [],
        timeZone: userTimeZone,
      };

      const res = await fetch(`${BASE_URL}/auth/userinfo/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify(postBody),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        toast.error(t("auth.sessionExpiredLoginAgain"));
        navigate("/sign-in");
        return;
      }

      if (!res.ok) {
        throw new Error(extractErrorMessage(data, t("auth.failedToSaveBiography")));
      }

      localStorage.setItem("salonData", JSON.stringify(postBody));
      localStorage.setItem("user_currency", currency);
      localStorage.setItem("user_country", selectedCountryRef.current);

      sessionStorage.setItem("onboarding_force_route", "/biography-member");
      sessionStorage.setItem("onboarding_force_status", "salon");

      toast.success(t("auth.salonInfoSaved"));
      navigate("/biography-member", { replace: true });
    } catch (err) {
      console.error("Biography salon error:", err);
      toast.error(err.message || t("auth.failedToSaveBiography"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (
        e.key === "Enter" &&
        !loading &&
        fullName.trim() &&
        companyName.trim() &&
        phone.trim()
      ) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [loading, fullName, companyName, phone]);

  return (
    <motion.div
      className="biography"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="biography-main">
        <motion.div
          className="biography-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="biography-header">
            <div className="biography-text">
              <h2>{t("auth.biographyHeader")}</h2>
              <p>{t("auth.biographySubheader")}</p>
            </div>
          </div>
          <div className="biography-content">
            <div className="biography-form" ref={dropdownRef}>
              <LanguageSwitcher />
              <div className="biography-form-group">
                <label>{t("auth.fullNameLabel")}</label>
                <input
                  type="text"
                  placeholder={t("auth.fullNamePlaceholder")}
                  value={fullName}
                  onChange={handleFullNameChange}
                  className={errors.fullName ? "invalid" : ""}
                  autoFocus
                />
                {errors.fullName && (
                  <span className="input-error-text">{errors.fullName}</span>
                )}
              </div>

              <div className="biography-form-group">
                <label>{t("auth.companyNameLabel")} *</label>
                <input
                  type="text"
                  placeholder={t("auth.companyNamePlaceholder")}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={errors.companyName ? "invalid" : ""}
                />
                {errors.companyName && (
                  <span className="input-error-text">{errors.companyName}</span>
                )}
              </div>

              <PhoneField
                phone={phone}
                error={errors.phone}
                onPhoneChange={handlePhoneChange}
                onCountryChange={(c) => {
                  selectedCountryRef.current = c;
                }}
              />

              <div className="biography-form-group biography-form-group-currency">
                <label>{t("auth.currencyLabel")}</label>

                <div
                  className={`currency-field ${errors.currency ? "invalid" : ""}`}
                  onClick={() => {
                    if (!loadingCurrency) {
                      setOpenCurrency((prev) => !prev);
                    }
                  }}
                >
                  <span className="currency-display">
                    {loadingCurrency ? (
                      t("auth.loading")
                    ) : (
                      <>
                        {currencies.find((c) => c.code === currency)?.flag && (
                          <img
                            src={currencies.find((c) => c.code === currency)?.flag}
                            alt={currency}
                            className="currency-flag"
                            style={{
                              width: 20,
                              height: 14,
                              marginRight: 6,
                              objectFit: "contain",
                            }}
                          />
                        )}
                        {currency || t("auth.selectCurrency")}
                      </>
                    )}
                  </span>

                  <svg
                    className={`currency-down ${openCurrency ? "open" : ""}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M7 10l5 5 5-5"
                      stroke="#9A99AB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                {openCurrency && currencies.length > 0 && (
                  <div className="currency-menu">
                    {currencies.map((item) => (
                      <div
                        key={item.code}
                        className="currency-item"
                        onClick={() => {
                          setCurrency(item.code);
                          setOpenCurrency(false);
                        }}
                      >
                        {item.flag && (
                          <img
                            src={item.flag}
                            alt={item.name}
                            className="currency-flag"
                          />
                        )}
                        <span className="currency-name">{item.name}</span>
                        <div
                          className={`currency-radio ${
                            currency === item.code ? "selected" : ""
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {errors.currency && (
                  <span className="input-error-text">{errors.currency}</span>
                )}
              </div>
            </div>

            <div className="flex flex-col w-full md:w-[50%] h-full   self-center" >
              <div className="location-selector-wrapper" >
                <LocationSelector
                  position={position}
                  setPosition={setPosition}
                  setAddressDetails={setAddressDetails}
                />
              </div>
              {errors.location && (
                <span className="input-error-text mt-2">{errors.location}</span>
              )}
            </div>
          </div>

          {userStatus !== "salon" && (
            <div className="biography-work">
              <label className="biography-work-title">{t("auth.workTitle")}</label>

              <div
                className={`biography-checkbox-group ${errors.work ? "invalid" : ""}`}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={workOptions.studio}
                    onChange={() =>
                      setWorkOptions((prev) => ({
                        ...prev,
                        studio: !prev.studio,
                      }))
                    }
                  />
                  <span>{t("auth.studioOption")}</span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={workOptions.travel}
                    onChange={() =>
                      setWorkOptions((prev) => ({
                        ...prev,
                        travel: !prev.travel,
                      }))
                    }
                  />
                  <span>{t("auth.travelOption")}</span>
                </label>
              </div>

              {errors.work && (
                <span className="input-error-text">{errors.work}</span>
              )}
            </div>
          )}

          <div className="biography-btn-footer">
            <motion.button
              className="biography-next-btn"
              onClick={handleNext}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? t("auth.saving") : t("auth.nextButton")}
            </motion.button>

            <span className="biography-step">{t("auth.subNicheStep")}</span>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
}